import Icon, { EyeInvisibleOutlined, EyeTwoTone, KeyOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Form, Input, Row, message } from 'antd';
import { useContext, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';
import useResponsiveLayout from '../../../components/LayoutComponent/UseResponsiveLayout/UseResponsiveLayout';
import PublicLayoutComponent from '../../../components/PublicLayoutComponent/PublicLayoutComponent';
import CustomContext from '../../../contexts/CustomContext';
import { SignUpUser } from '../../../models/Entities';
import { ReactComponent as LogoIcon } from '../../../resources/images/agrisens-icon.svg';
import { ReactComponent as EmailIcon } from '../../../resources/images/email-icon.svg';
import { ReactComponent as LockIcon } from '../../../resources/images/lock-icon.svg';
import authService from '../../../services/AuthService';
import InfoComponent from '../InfoComponent/InfoComponent';
import styles from './SignUpPage.module.scss';

/**
 * Returns the sign up page.
 * @returns the sign up page.
 */
const SignUpPage: React.FC = () => {
    /***HOOKS */
    const [signUpUser, setSignUpUser] = useState<SignUpUser>();
    const [desktop] = useResponsiveLayout();

    return (
        <PublicLayoutComponent>
            <Col span={0} xl={12}>
                <InfoComponent />
            </Col>
            <Col span={24} xl={12} className={`${styles.form} ${desktop ? styles.desktop : styles.mobile}`}>
                {signUpUser ? <ConfirmSignUpForm signUpUser={signUpUser} /> : <SignUpForm setSignUpUser={setSignUpUser} />}
            </Col>
        </PublicLayoutComponent>
    );
};
export default SignUpPage;

/**
 * Returns the sign up form.
 * @returns the sign up form.
 */
const SignUpForm: React.FC<SignUpProps> = (props) => {
    const { setSignUpUser } = props;

    /*** HOOKS ***/

    const [form] = Form.useForm();
    const intl = useIntl();
    const [loading, setLoading] = useState<'loading'>();

    /*** METHODS ***/

    const signUp = async (values: any) => {
        try {
            setLoading('loading');

            const signUpUser: SignUpUser = Object.assign({}, values);
            await authService.signUp(signUpUser);
            setSignUpUser(signUpUser);
        } catch (error: any) {
            if (error.message && error.message === 'An account with the given email already exists.') {
                message.error(intl.formatMessage({ id: 'signUp.status.email.exists' }), 6);
            } else if (error.message) {
                message.error(error.message, 6);
            } else {
                message.error(intl.formatMessage({ id: 'status.internalError' }), 6);
            }
        } finally {
            setLoading(undefined);
        }
    };

    /*** VISUAL ***/

    return (
        <Form form={form} onFinish={signUp} colon={false} layout="vertical">
            <Row>
                <Col span={24} className={styles.panel}>
                    <LogoIcon className={styles.logo} />
                </Col>
            </Row>
            <Row>
                <Col span={24} className={styles.panel}>
                    <div className={styles.getStarted}>
                        <FormattedMessage id="signUp.getStarted" />
                    </div>
                </Col>
            </Row>
            <Row>
                <Col span={24} className={styles.panel}>
                    <div className={styles.createAcc}>
                        <FormattedMessage id="signUp.createAccount" />
                    </div>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Form.Item
                        name="username"
                        rules={[
                            { required: true, message: <FormattedMessage id="status.mandatory" /> },
                            { type: 'email', message: <FormattedMessage id="status.email.invalid" /> }
                        ]}
                        required={false}
                    >
                        <Input
                            maxLength={100}
                            size="large"
                            placeholder={intl.formatMessage({ id: 'signUp.email' })}
                            prefix={<Icon component={EmailIcon} className={styles.icon} />}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Form.Item
                        name="password"
                        rules={[
                            { required: true, message: <FormattedMessage id="status.mandatory" /> },
                            {
                                pattern: /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-_.,;]).{8,}$/,
                                message: <FormattedMessage id="status.password" />
                            }
                        ]}
                        required={false}
                    >
                        <Input.Password
                            type="password"
                            size="large"
                            maxLength={25}
                            placeholder={intl.formatMessage({ id: 'signUp.password' })}
                            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                            prefix={<Icon component={LockIcon} className={styles.icon} />}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={24} className={styles.legal}>
                    <Form.Item
                        name="legal"
                        valuePropName="checked"
                        initialValue={false}
                        rules={[
                            {
                                required: true,
                                message: <FormattedMessage id="signUp.consent.error.required" />,
                                transform: (value) => value || undefined,
                                type: 'boolean'
                            }
                        ]}
                    >
                        <Checkbox data-test="legal">
                            <FormattedMessage id="signUp.consent" />{' '}
                            <Link to="/legal-notice" className={styles.privacyLink}>
                                <FormattedMessage id="signUp.legalNotice" />
                            </Link>{' '}
                            <FormattedMessage id="common.and" />{' '}
                            <Link to="/privacy-policy" className={styles.privacyLink}>
                                <FormattedMessage id="signUp.privacyPolicy" />
                            </Link>
                        </Checkbox>
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={24} className={styles.panel}>
                    <span className={styles.haveAccountText}>
                        <FormattedMessage id="signUp.haveAnAccount" />
                    </span>
                    <a href="/signin" className={styles.login}>
                        <FormattedMessage id="signUp.login" />
                    </a>
                </Col>
            </Row>
            <Row className={styles.panel}>
                <Col span={14} className={styles.button}>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" size="large" loading={loading === 'loading'} block className={styles.signupButton}>
                            <FormattedMessage id="signUp.submit" tagName="span" />
                        </Button>
                    </Form.Item>
                </Col>
            </Row>
        </Form>
    );
};

interface SignUpProps {
    setSignUpUser: (signUpUser: SignUpUser) => void;
}

const ConfirmSignUpForm: React.FC<CompleteNewPasswordProps> = (props) => {
    const { signUpUser } = props;

    /*** HOOKS ***/
    const context = useContext(CustomContext);
    const [form] = Form.useForm();
    const intl = useIntl();
    const navigate = useNavigate();
    const [loading, setLoading] = useState<'loading'>();

    /*** METHODS ***/

    const confirmSignUp = async (values: any) => {
        try {
            setLoading('loading');
            const auth = await authService.confirmSignUp(signUpUser, values.code);
            context.setAuth(auth);
            navigate('/');
        } catch (error: any) {
            if (error.message && error.message === 'Invalid verification code provided, please try again.') {
                message.error(intl.formatMessage({ id: 'signUp.status.code.invalid' }), 6);
            } else if (error.message) {
                message.error(error.message, 6);
            } else {
                message.error(intl.formatMessage({ id: 'status.internalError' }), 6);
            }
        } finally {
            setLoading(undefined);
        }
    };

    /*** VISUAL ***/

    return (
        <Form form={form} onFinish={confirmSignUp} colon={false} layout="vertical">
            <Row>
                <Col span={24} className={styles.panel}>
                    <LogoIcon className={styles.logo} />
                </Col>
            </Row>
            <Row>
                <Col span={24} className={styles.panel}>
                    <div className={styles.title}>
                        <FormattedMessage id="signUp.title.confirmSignUp" />
                    </div>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Form.Item name="code" rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]} required={false}>
                        <Input
                            maxLength={20}
                            size="large"
                            placeholder={intl.formatMessage({ id: 'signUp.code' })}
                            prefix={<KeyOutlined className={styles.defaultIcon} />}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={8} className={styles.button}>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" size="large" loading={loading === 'loading'} block className={styles.signupButton}>
                            <FormattedMessage id="signUp.submit.confirmSignUp" tagName="span" />
                        </Button>
                    </Form.Item>
                </Col>
            </Row>
        </Form>
    );
};
interface CompleteNewPasswordProps {
    signUpUser: SignUpUser;
}
